import React, { useState, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Monitor, Cpu, Code, ChevronRight, X, BookOpen, Send, CheckCircle, Loader2, User, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import emailjs from '@emailjs/browser';

const Courses = () => {
  const { t } = useLanguage();
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [applyCourse, setApplyCourse] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const formRef = useRef();
  const [formData, setFormData] = useState({
    name: '',
    phone: '+994'
  });

  const iconMap = {
    monitor: Monitor,
    cpu: Cpu,
    code: Code,
    plane: Monitor,
    brain: Cpu,
    graduationcap: BookOpen
  };

  const courseImages = [
    'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600&q=80',
    'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=600&q=80',
    'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=600&q=80',
    'https://images.unsplash.com/photo-1473968512647-3e447244af8f?w=600&q=80',
    'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&q=80',
    'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=600&q=80'
  ];

  const openModal = (course) => {
    setSelectedCourse(course);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedCourse(null);
    document.body.style.overflow = 'unset';
  };

  const openApplyModal = (course) => {
    setApplyCourse(course);
    setIsSuccess(false);
    setFormData({ name: '', phone: '+994' });
    document.body.style.overflow = 'hidden';
  };

  const closeApplyModal = () => {
    setApplyCourse(null);
    setIsSuccess(false);
    document.body.style.overflow = 'unset';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value;
    value = value.replace(/[^\d+]/g, '');
    if (!value.startsWith('+994')) {
      if (value.startsWith('994')) {
        value = '+' + value;
      } else if (value.startsWith('+')) {
        value = '+994';
      } else {
        value = '+994' + value.replace(/^\+/, '');
      }
    }
    setFormData(prev => ({ ...prev, phone: value }));
  };

  const isAz = t.nav.courses === 'Kurslar';

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setIsSubmitting(true);

    const templateParams = {
      name: formData.name,
      phone: formData.phone,
      course: applyCourse.title
    };

    try {
      await emailjs.send(
        'service_ruslan281',
        'template_ruslan281',
        templateParams,
        'BZ_-xJVHpD9z5ooYv'
      );
      setIsSuccess(true);
      setFormData({ name: '', phone: '+994' });
      setTimeout(() => closeApplyModal(), 3000);
    } catch (error) {
      console.error('Email send error:', error);
      alert(isAz
        ? 'Xəta baş verdi. Zəhmət olmasa yenidən cəhd edin.'
        : 'An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formTexts = {
    title: isAz ? 'Kursa Müraciət' : 'Course Application',
    name: isAz ? 'Ad, Soyad' : 'Full Name',
    namePlaceholder: isAz ? 'Adınızı daxil edin' : 'Enter your name',
    phone: isAz ? 'Telefon' : 'Phone',
    phonePlaceholder: '+994 XX XXX XX XX',
    submit: isAz ? 'Müraciət Et' : 'Apply Now',
    submitting: isAz ? 'Göndərilir...' : 'Sending...',
    success: isAz ? 'Müraciətiniz göndərildi!' : 'Application sent!',
    successDesc: isAz ? 'Tezliklə sizinlə əlaqə saxlanılacaq.' : 'We will contact you soon.'
  };

  return (
    <section id="courses" className="py-24 bg-gradient-to-b from-white to-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="inline-block px-4 py-1.5 bg-sky-100 text-sky-600 text-sm font-medium rounded-full mb-4">
            {t.courses.title}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
            {t.courses.subtitle}
          </h2>
        </div>

        {/* Courses Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {t.courses.items.map((course, index) => {
            const Icon = iconMap[course.icon];
            return (
              <div
                key={course.id}
                className="group bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 flex flex-col"
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={courseImages[index]}
                    alt={course.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
                  
                  {/* Icon Badge */}
                  <div className="absolute top-4 left-4">
                    <div className="w-12 h-12 bg-white/90 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-lg">
                      {Icon ? <Icon className="w-6 h-6 text-sky-500" /> : <BookOpen className="w-6 h-6 text-sky-500" />}
                    </div>
                  </div>

                  {/* Title on Image */}
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-xl font-bold text-white">{course.title}</h3>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 flex flex-col flex-grow">
                  <p className="text-slate-600 mb-4 leading-relaxed text-sm line-clamp-2">
                    {course.description}
                  </p>

                  {/* Features */}
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    {course.features.slice(0, 4).map((feature, fIndex) => (
                      <div key={fIndex} className="flex items-center text-xs text-slate-500">
                        <ChevronRight className="w-3 h-3 text-sky-400 mr-1 flex-shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Pricing */}
                  {course.pricing && (
                    <div className="mb-4 p-3 bg-slate-50 rounded-xl space-y-2">
                      {course.pricing.map((price, pIndex) => (
                          <div key={pIndex} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                          {price.label && (
                            <span className="text-xs font-medium text-slate-500">{price.label}</span>
                          )}
                          <div className={`flex items-center gap-2 ${!price.label ? 'ml-auto' : ''}`}>
                            <span className="text-sm text-red-400 line-through font-medium">
                              {price.oldPrice}{t.courses.currency}
                            </span>
                            <span className="text-lg font-bold text-sky-600">
                              {price.newPrice}{t.courses.currency}
                            </span>
                            <span className="text-xs text-slate-400">{t.courses.perMonth}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Buttons */}
                  <div className="mt-auto space-y-2">
                    <Button
                      onClick={() => openModal(course)}
                      variant="outline"
                      className="w-full border-sky-200 text-sky-600 hover:bg-sky-50 hover:border-sky-300 transition-all"
                    >
                      <BookOpen className="w-4 h-4 mr-2" />
                      {t.courses.detailBtn}
                    </Button>
                    <Button
                      onClick={() => openApplyModal(course)}
                      className="w-full bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600 text-white transition-all"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      {t.courses.applyBtn}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Course Detail Modal */}
      {selectedCourse && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"></div>
          <div 
            className="relative bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative h-48 sm:h-56">
              <img
                src={courseImages[t.courses.items.findIndex(c => c.id === selectedCourse.id)]}
                alt={selectedCourse.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-transparent"></div>
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors shadow-lg"
              >
                <X className="w-5 h-5 text-slate-700" />
              </button>
              <div className="absolute bottom-4 left-6 right-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg">
                    {iconMap[selectedCourse.icon] && 
                      React.createElement(iconMap[selectedCourse.icon], { className: "w-6 h-6 text-sky-500" })
                    }
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-white">{selectedCourse.title}</h3>
                </div>
              </div>
            </div>
            <div className="p-6 sm:p-8 overflow-y-auto max-h-[50vh]">
              <div className="flex flex-wrap gap-2 mb-6">
                {selectedCourse.features.map((feature, index) => (
                  <span 
                    key={index}
                    className="px-3 py-1.5 bg-sky-50 text-sky-600 text-sm font-medium rounded-full"
                  >
                    {feature}
                  </span>
                ))}
              </div>
              <div className="prose prose-slate max-w-none">
                <p className="text-slate-600 leading-relaxed text-base">
                  {selectedCourse.fullDescription}
                </p>
              </div>
            </div>
            <div className="p-6 sm:p-8 pt-0">
              <Button
                onClick={closeModal}
                className="w-full bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600 text-white py-6 rounded-xl shadow-lg"
              >
                {t.courses.closeBtn}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Apply Modal */}
      {applyCourse && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={closeApplyModal}
        >
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"></div>
          <div 
            className="relative bg-white rounded-3xl max-w-md w-full overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-sky-500 to-cyan-500 p-6">
              <button
                onClick={closeApplyModal}
                className="absolute top-4 right-4 w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
              >
                <X className="w-4 h-4 text-white" />
              </button>
              <h3 className="text-xl font-bold text-white">{formTexts.title}</h3>
              <p className="text-sky-100 text-sm mt-1">{applyCourse.title}</p>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {isSuccess ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                  <h4 className="text-lg font-bold text-slate-800 mb-2">{formTexts.success}</h4>
                  <p className="text-slate-500 text-sm">{formTexts.successDesc}</p>
                </div>
              ) : (
                <div ref={formRef} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="apply-name" className="text-slate-700 font-medium flex items-center">
                      <User className="w-4 h-4 mr-2 text-slate-400" />
                      {formTexts.name}
                    </Label>
                    <Input
                      id="apply-name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder={formTexts.namePlaceholder}
                      className="h-12 rounded-xl border-slate-200 focus:border-sky-500 focus:ring-sky-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="apply-phone" className="text-slate-700 font-medium flex items-center">
                      <Phone className="w-4 h-4 mr-2 text-slate-400" />
                      {formTexts.phone}
                    </Label>
                    <Input
                      id="apply-phone"
                      name="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      placeholder={formTexts.phonePlaceholder}
                      className="h-12 rounded-xl border-slate-200 focus:border-sky-500 focus:ring-sky-500"
                    />
                  </div>
                  <Button
                    onClick={handleSubmit}
                    disabled={isSubmitting || !formData.name}
                    className="w-full h-12 bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600 text-white rounded-xl shadow-lg transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {formTexts.submitting}
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        {formTexts.submit}
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Courses;
